import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import Papa from "papaparse";
import CreateBulkTagComponent from "./component";

const CreateBulkTagContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();
  
  const [currentView, setCurrentView] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);
  const [selectedTags, setSelectedTags] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [bulkUploadHeading, setBulkUploadHeading] = useState("Upload Tag in bulk");
  
  // Store added tags for activation
  const [addedTagsData, setAddedTagsData] = useState(null);

  // Handle CSV file upload as FormData
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setCurrentView('upload');
    
    const formData = new FormData();
    formData.append('file', file);
    
    const interval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 10, 90));
    }, 200);
    
    props.getDataFromAPI(
      `/partner/api/v2/corporates/${corporateId}/tag/bulk`,
      "POST",
      formData,
      (response) => {
        clearInterval(interval);
        setUploadProgress(100);
        
        console.log("Bulk Upload API Response:", response);
        
        const apiResponse = response?.data || response || [];
        
        if (apiResponse.length === 0) {
          toastr.warning("Warning", "No tags were created");
          setUploading(false);
          setCurrentView('upload');
          return;
        }
        
        const processedData = apiResponse.map((dept, index) => {
          const deptId = dept.department_id;
          const tags = dept.tags || [];
          
          return {
            department_id: deptId,
            department_name: `Department ${deptId}`,
            tags: tags.map(tag => ({
              id: `tag_${tag.uid}_${Date.now()}_${Math.random()}`,
              uid: tag.uid,
              token: tag.token,
              status: "new",
              balance: 0,
              department_id: deptId,
              department_name: `Department ${deptId}`
            }))
          };
        });
        
        setUploadedData(processedData);
        setSelectedTags({});
        setUploadResult({
          success: true,
          message: `${apiResponse.reduce((sum, dept) => sum + (dept.tags?.length || 0), 0)} tags uploaded successfully`,
          count: apiResponse.reduce((sum, dept) => sum + (dept.tags?.length || 0), 0)
        });
        setShowSuccessPopup(true);
        setUploading(false);
      },
      (err) => {
        clearInterval(interval);
        console.log("Bulk Upload API Error:", err);
        setUploadProgress(0);
        setShowSuccessPopup(false);
        setUploadResult({
          success: false,
          message: err?.message || err?.error || "Failed to upload file"
        });
        setUploading(false);
        toastr.error("Error", err?.message || "Failed to upload file");
      }
    );
  };

  // Handle file select - change heading
  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
      toastr.error("Error", "Please upload a CSV file only");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toastr.error("Error", "File size should be less than 5MB");
      return;
    }
    setBulkUploadHeading("Uploaded");
  };

  // Handle cancel file - reset heading
  const handleCancelFile = () => {
    setBulkUploadHeading("Upload Tag in bulk");
  };

  // Handle tag selection
  const handleToggleTagSelection = (tags, action) => {
    if (action === 'clear') {
      setSelectedTags({});
    } else if (action === 'all') {
      setSelectedTags(tags);
    } else if (action === 'single') {
      setSelectedTags(prev => {
        const newSelected = { ...prev };
        const tagId = Object.keys(tags)[0];
        if (newSelected[tagId]) {
          delete newSelected[tagId];
        } else {
          newSelected[tagId] = tags[tagId];
        }
        return newSelected;
      });
    } else if (action === 'department') {
      setSelectedTags(prev => {
        const newSelected = { ...prev };
        const tagIds = Object.keys(tags);
        const allSelected = tagIds.every(id => prev[id]);
        
        if (allSelected) {
          tagIds.forEach(id => delete newSelected[id]);
        } else {
          tagIds.forEach(id => {
            newSelected[id] = tags[id];
          });
        }
        return newSelected;
      });
    }
  };

  // Group selected tags by department for Add API (POST /tag/add)
  const groupTagsForAddAPI = (selectedTagsList) => {
    const grouped = {};
    
    Object.values(selectedTagsList).forEach(tag => {
      let deptId = tag.department_id;
      
      if (deptId === undefined || deptId === null) {
        deptId = 0;
      }
      
      if (typeof deptId === 'string' && !isNaN(parseInt(deptId))) {
        deptId = parseInt(deptId);
      }
      
      if (!grouped[deptId]) {
        grouped[deptId] = {
          department_id: deptId,
          tags: []
        };
      }
      
      grouped[deptId].tags.push({
        uid: tag.uid,
        token: tag.token
      });
    });
    
    const result = Object.values(grouped);
    console.log("Add API Request Body:", JSON.stringify(result, null, 2));
    return result;
  };

  // Group selected tags for Activate API (PUT /tag/bulk/activate)
  const groupTagsForActivateAPI = (selectedTagsList) => {
    const grouped = {};
    
    Object.values(selectedTagsList).forEach(tag => {
      let deptId = tag.department_id;
      
      if (deptId === undefined || deptId === null) {
        deptId = 0;
      }
      
      if (typeof deptId === 'string' && !isNaN(parseInt(deptId))) {
        deptId = parseInt(deptId);
      }
      
      if (!grouped[deptId]) {
        grouped[deptId] = {
          departmentId: deptId,
          uids: []
        };
      }
      
      const uid = tag.uid;
      if (uid && !grouped[deptId].uids.includes(uid)) {
        grouped[deptId].uids.push(uid);
      }
    });
    
    const result = Object.values(grouped);
    console.log("Activate API Request Body:", JSON.stringify(result, null, 2));
    return result;
  };

  // Handle Add More button click - calls ADD API only
  const handleAddMoreClick = () => {
    const tagIds = Object.keys(selectedTags);
    if (tagIds.length === 0) {
      toastr.warning("No Selection", "Please select tags to add");
      return;
    }
    
    setAddLoading(true);
    
    const requestBody = groupTagsForAddAPI(selectedTags);
    
    console.log("=== ADD MORE - ADD API REQUEST ===");
    console.log("URL:", `/partner/api/v2/corporates/${corporateId}/tag/add`);
    console.log("Payload:", JSON.stringify(requestBody, null, 2));
    
    if (requestBody.length === 0) {
      toastr.error("Error", "Unable to process tags. No valid department IDs found.");
      setAddLoading(false);
      return;
    }
    
    props.getDataFromAPI(
      `/partner/api/v2/corporates/${corporateId}/tag/add`,
      "POST",
      requestBody,
      (response) => {
        setAddLoading(false);
        console.log("Add API Response:", response);
        
        const data = response?.data || response;
        const successCount = data?.success_count || tagIds.length;
        const failureCount = data?.failure_count || 0;
        
        // Store the successfully added tags for potential activation
        const addedTags = Object.values(selectedTags).filter(tag => {
          return !data?.failed_details?.some(failed => failed.uid === tag.uid);
        });
        
        setAddedTagsData(addedTags);
        
        setResultData({
          type: 'add',
          success: true,
          successCount: successCount,
          failureCount: failureCount,
          totalCount: tagIds.length,
          failedDetails: data?.failed_details || []
        });
        setShowResultPopup(true);
        
        if (successCount > 0) {
          toastr.success("Success", `${successCount} tags added successfully`);
        }
        if (failureCount > 0) {
          toastr.warning("Warning", `${failureCount} tags failed to add`);
        }
        
        // Refresh the uploaded data to reflect added tags
        // But don't redirect, stay on same page
      },
      (err) => {
        setAddLoading(false);
        console.error("Add API Error:", err);
        setResultData({
          type: 'add',
          success: false,
          message: err?.message || err?.error || "Failed to add tags",
          totalCount: tagIds.length
        });
        setShowResultPopup(true);
        toastr.error("Error", err?.message || "Failed to add tags");
      }
    );
  };

  // Handle Add & Activate - First ADD, then ACTIVATE
  const handleAddAndActivate = () => {
    const tagIds = Object.keys(selectedTags);
    if (tagIds.length === 0) {
      toastr.warning("No Selection", "Please select tags to add and activate");
      return;
    }
    
    setActivateLoading(true);
    
    // Step 1: First call ADD API
    const addRequestBody = groupTagsForAddAPI(selectedTags);
    
    console.log("=== ADD & ACTIVATE - STEP 1: ADD API REQUEST ===");
    console.log("URL:", `/partner/api/v2/corporates/${corporateId}/tag/add`);
    console.log("Payload:", JSON.stringify(addRequestBody, null, 2));
    
    if (addRequestBody.length === 0) {
      toastr.error("Error", "Unable to process tags. No valid department IDs found.");
      setActivateLoading(false);
      return;
    }
    
    props.getDataFromAPI(
      `/partner/api/v2/corporates/${corporateId}/tag/add`,
      "POST",
      addRequestBody,
      (addResponse) => {
        console.log("ADD API Response:", addResponse);
        
        const addData = addResponse?.data || addResponse;
        const addSuccessCount = addData?.success_count || tagIds.length;
        const addFailureCount = addData?.failure_count || 0;
        
        // Get successfully added tags for activation
        const successfullyAddedTags = Object.values(selectedTags).filter(tag => {
          return !addData?.failed_details?.some(failed => failed.uid === tag.uid);
        });
        
        if (successfullyAddedTags.length === 0) {
          // No tags were added successfully, show result and return
          setActivateLoading(false);
          setResultData({
            type: 'add',
            success: addSuccessCount > 0,
            successCount: addSuccessCount,
            failureCount: addFailureCount,
            totalCount: tagIds.length,
            failedDetails: addData?.failed_details || []
          });
          setShowResultPopup(true);
          
          if (addSuccessCount > 0) {
            toastr.success("Success", `${addSuccessCount} tags added successfully`);
          }
          if (addFailureCount > 0) {
            toastr.warning("Warning", `${addFailureCount} tags failed to add`);
          }
          return;
        }
        
        // Step 2: Call ACTIVATE API for successfully added tags
        const activateRequestBody = groupTagsForActivateAPI(successfullyAddedTags);
        
        console.log("=== ADD & ACTIVATE - STEP 2: ACTIVATE API REQUEST ===");
        console.log("URL:", `/partner/api/v2/corporates/${corporateId}/tag/bulk/activate`);
        console.log("Payload:", JSON.stringify(activateRequestBody, null, 2));
        
        if (activateRequestBody.length === 0) {
          setActivateLoading(false);
          setResultData({
            type: 'add',
            success: addSuccessCount > 0,
            successCount: addSuccessCount,
            failureCount: addFailureCount,
            totalCount: tagIds.length,
            failedDetails: addData?.failed_details || []
          });
          setShowResultPopup(true);
          return;
        }
        
        props.getDataFromAPI(
          `/partner/api/v2/corporates/${corporateId}/tag/bulk/activate`,
          "PUT",
          activateRequestBody,
          (activateResponse) => {
            setActivateLoading(false);
            
            const activateData = activateResponse?.data || activateResponse;
            const activateSuccessCount = activateData?.success_count || successfullyAddedTags.length;
            const activateFailureCount = activateData?.failure_count || 0;
            
            // Show combined result
            setResultData({
              type: 'both',
              success: true,
              addSuccessCount: addSuccessCount,
              addFailureCount: addFailureCount,
              activateSuccessCount: activateSuccessCount,
              activateFailureCount: activateFailureCount,
              totalCount: tagIds.length,
              addFailedDetails: addData?.failed_details || [],
              activateFailedDetails: activateData?.failed_details || []
            });
            setShowResultPopup(true);
            
            if (addSuccessCount > 0) {
              toastr.success("Success", `${addSuccessCount} tags added successfully`);
            }
            if (activateSuccessCount > 0) {
              toastr.success("Success", `${activateSuccessCount} tags activated successfully`);
            }
            if (addFailureCount > 0) {
              toastr.warning("Warning", `${addFailureCount} tags failed to add`);
            }
            if (activateFailureCount > 0) {
              toastr.warning("Warning", `${activateFailureCount} tags failed to activate`);
            }
            
            // Navigate to dashboard after successful add and activate
            navigate(`/home/walletandtags/tagpay-dashboard/${corporateId}`);
          },
          (activateErr) => {
            setActivateLoading(false);
            console.error("Activate API Error:", activateErr);
            
            // Show result with add success but activate failure
            setResultData({
              type: 'add',
              success: addSuccessCount > 0,
              successCount: addSuccessCount,
              failureCount: addFailureCount,
              totalCount: tagIds.length,
              failedDetails: addData?.failed_details || [],
              activateError: activateErr?.message || "Failed to activate tags"
            });
            setShowResultPopup(true);
            
            if (addSuccessCount > 0) {
              toastr.success("Success", `${addSuccessCount} tags added successfully`);
              toastr.warning("Warning", "Tags added but activation failed. Please activate manually.");
            }
          }
        );
      },
      (addErr) => {
        setActivateLoading(false);
        console.error("Add API Error:", addErr);
        
        setResultData({
          type: 'add',
          success: false,
          message: addErr?.message || addErr?.error || "Failed to add tags",
          totalCount: tagIds.length
        });
        setShowResultPopup(true);
        toastr.error("Error", addErr?.message || "Failed to add tags");
      }
    );
  };

  // Handle redirect after popup close - ONLY for success case
  const handleCloseResultPopup = () => {
    const wasSuccess = resultData?.success === true;
    
    setShowResultPopup(false);
    
    // Only redirect if the operation was successful for single add
    if (wasSuccess && resultData?.type === 'add') {
       navigate(`/home/walletandtags/tagpay-dashboard/${corporateId}`);
    }
    
    // Reset result data
    setResultData(null);
    setAddedTagsData(null);
  };

  // Download sample CSV
  const handleDownloadSample = () => {
    const sampleData = [
      { department_id: 8, uid: "9000001000", token: "TKN00000X" },
      { department_id: 8, uid: "9000001001", token: "TKN00001X" },
      { department_id: 8, uid: "9000001002", token: "TKN00002X" }
    ];
    
    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_tags_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
    toastr.success("Download Started", "Sample CSV file downloaded");
  };

  const handleBackClick = () => {
    setCurrentView('upload');
    setUploadedData(null);
    setUploadResult(null);
    setSelectedTags({});
    setShowSuccessPopup(false);
    setBulkUploadHeading("Upload Tag in bulk");
    navigate(-1);
  };

  const handleAddMoreTags = () => {
    handleAddMoreClick();
  };

  const handleGoToDashboard = () => {
    navigate(`/home/walletandtags/tagpay-dashboard/${corporateId}`);
  };

  const handleRetry = () => {
    setCurrentView('upload');
    setUploadResult(null);
    setUploadedData(null);
    setSelectedTags({});
    setShowSuccessPopup(false);
    setBulkUploadHeading("Upload Tag in bulk");
  };

  const handlePopupOk = () => {
    setShowSuccessPopup(false);
    setCurrentView('success');
  };

  return (
    <CreateBulkTagComponent
      corporateId={corporateId}
      currentView={currentView}
      uploading={uploading}
      uploadProgress={uploadProgress}
      uploadResult={uploadResult}
      uploadedData={uploadedData}
      selectedTags={selectedTags}
      showSuccessPopup={showSuccessPopup}
      addLoading={addLoading}
      activateLoading={activateLoading}
      showResultPopup={showResultPopup}
      resultData={resultData}
      bulkUploadHeading={bulkUploadHeading}
      onFileUpload={handleFileUpload}
      onFileSelect={handleFileSelect}
      onCancelFile={handleCancelFile}
      onBackClick={handleBackClick}
      onDownloadSample={handleDownloadSample}
      onAddMoreTags={handleAddMoreTags}
      onAddAndActivate={handleAddAndActivate}
      onGoToDashboard={handleGoToDashboard}
      onRetry={handleRetry}
      onToggleTagSelection={handleToggleTagSelection}
      onPopupOk={handlePopupOk}
      onCloseResultPopup={handleCloseResultPopup}
    />
  );
};

function mapStateToProps(state) {
  return {
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CreateBulkTagContainer);